import React from 'react';
import { PapiUser } from '@wix/da-papi-types';
import { URLS } from '@wix/da-url';
import { useTranslation } from 'react-i18next';
import {
  BiContextProvidedValue,
  BiData,
  AddToGroupSelectBiEvent,
  DefaultBiEvent,
} from '@wix/da-bi/pkg/events';
import ArrowRight from '@wix/da-ds/pkg/Icons/ArrowRight';
import { ListItemButton } from '@wix/da-ds/pkg/List/ListItem';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import StreamedList from '../../../StreamedList';
import BasicUserAvatar from '../../../User/_partials/BasicUserAvatar';
import s from './GroupsList.scss';

export interface Props {
  headerContent?: React.ReactNode;
  deviationId: number;
  streamId: string;
  groups: PapiUser[];
  isFetching: boolean;
  filterValue?: string;
  scrollableWrapperClassName?: string;
  onSelect: (group: PapiUser) => void;
}

const GroupsList: React.FC<Props> = ({
  headerContent,
  groups,
  onSelect,
  isFetching,
  deviationId,
  streamId,
  filterValue,
}) => {
  const { t } = useTranslation();
  const isEmpty = !isFetching && groups.length === 0;

  return isFetching && !groups.length ? (
    <LoadingIndicator view="llama" className={s['loader']} />
  ) : (
    <>
      {headerContent}
      {isEmpty ? (
        <Section className={s['empty']}>
          <Heading className={s['empty-header']}>
            {t('addToGroupPopup.empty.header')}
          </Heading>
          <div className={s['empty-text']}>
            {t('addToGroupPopup.empty.text')}
          </div>
          <GhostButton
            className={s['empty-cta-button']}
            variant="approval-green"
            size="medium"
            href={URLS.GROUPS}
            biData={BiData<DefaultBiEvent>({
              evid: 2,
              click_info: 'addToGroup.emptyState',
              sectionname: BiContextProvidedValue,
              widgetname: BiContextProvidedValue,
              typeid: BiContextProvidedValue,
              itemid: BiContextProvidedValue,
            })}
          >
            {t('addToGroupPopup.empty.CTA.button')}
          </GhostButton>
        </Section>
      ) : (
        <StreamedList
          streamId={streamId}
          className={s['list']}
          wrapperClassName={s['scrollable']}
        >
          {groups.map(group =>
            !filterValue ||
            group.username.toLowerCase().includes(filterValue.toLowerCase()) ? (
              <ListItemButton
                key={group.userId}
                onClick={() => onSelect(group)}
                biData={BiData<AddToGroupSelectBiEvent>({
                  evid: 341,
                  deviationid: deviationId,
                  groupid: group.useridUuid,
                  source_type: BiContextProvidedValue,
                })}
                label={group.username}
                prefix={<BasicUserAvatar user={group} size={35} />}
                suffixIcon={ArrowRight}
                fillContainerWidth
                showSuffixOnlyOnHover
              />
            ) : null
          )}
        </StreamedList>
      )}
    </>
  );
};
GroupsList.displayName = 'GroupsList';

export default GroupsList;
